import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import "./attendence.css";

export default function WorkingHoursReport() {
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewType, setViewType] = useState("grid");
    const [sortBy, setSortBy] = useState("name");
    const token = localStorage.getItem("accessToken");
    const intervalRef = useRef(null);

    // Fixed Break: 12:50 PM → 1:30 PM
    const BREAK_START = 770; // 12:50 PM in minutes
    const BREAK_END = 810;   // 01:30 PM in minutes

    const timeToMinutes = (timeStr) => {
        if (!timeStr || timeStr === "—" || !timeStr.includes(" ")) return null;
        const [time, period] = timeStr.trim().split(" ");
        let [h, m] = time.split(":").map(Number);
        if (period === "PM" && h !== 12) h += 12;
        if (period === "AM" && h === 12) h = 0;
        return h * 60 + m;
    };

    const formatDuration = (seconds) => {
        if (!seconds || seconds < 0) seconds = 0;
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        const s = seconds % 60;
        return `${h}h ${m}m ${s}s`;
    };

    const isInBreakTime = (minutes) => minutes >= BREAK_START && minutes < BREAK_END;

    const calculateLiveSeconds = (clockIn, clockOut, lastPunch) => {
        let totalSeconds = 0;
        const now = new Date();
        const nowMinutes = now.getHours() * 60 + now.getMinutes();
        const nowSecs = now.getSeconds();

        const inMin = timeToMinutes(clockIn);
        const outMin = timeToMinutes(clockOut);
        const lastMin = timeToMinutes(lastPunch);

        if (inMin !== null && outMin !== null && inMin < outMin) {
            for (let m = inMin; m < outMin; m++) {
                if (!isInBreakTime(m % 1440)) totalSeconds += 60;
            }
        }

        if (lastPunch && lastMin !== null && (!outMin || lastMin > outMin)) {
            const start = lastMin;
            for (let m = start; m < nowMinutes; m++) {
                if (!isInBreakTime(m % 1440)) totalSeconds += 60;
            }
            totalSeconds += nowSecs;
        }

        return totalSeconds;
    };

    const fetchRealTimeAttendance = async () => {
        if (!token) {
            setLoading(false);
            return;
        }

        try {
            const res = await axios.get(
                `${process.env.REACT_APP_API}/api/attendance/attendance/today/`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            const data = res.data || [];

            const processed = data.map(item => {
                const emp = item.employee;
                const lastPunch = item.last_punch || "—";
                const clockIn = item.clock_in || "—";
                const clockOut = item.clock_out || "—";

                const isClockedIn = lastPunch !== "—" &&
                    (!clockOut || timeToMinutes(lastPunch) > timeToMinutes(clockOut));

                const liveSeconds = calculateLiveSeconds(clockIn, clockOut, lastPunch);

                return {
                    id: emp.id,
                    name: emp.name,
                    code: emp.employee_code,
                    photo: emp.profile_photo || "/assets/user.png",
                    position: emp.job_position?.job_title || "N/A",
                    lastPunch,
                    clockIn,
                    clockOut,
                    isClockedIn,
                    liveSeconds,
                };
            });

            setEmployees(processed);
            setLoading(false);
        } catch (err) {
            console.error("Error fetching real-time data:", err);
            setLoading(false);
        }
    };

    useEffect(() => {
        const tick = () => {
            setEmployees(prev => prev.map(emp => ({
                ...emp,
                liveSeconds: calculateLiveSeconds(emp.clockIn, emp.clockOut, emp.lastPunch)
            })));
        };

        tick();
        intervalRef.current = setInterval(tick, 1000);
        return () => clearInterval(intervalRef.current);
    }, []);

    useEffect(() => {
        fetchRealTimeAttendance();
        const interval = setInterval(fetchRealTimeAttendance, 120000);
        return () => clearInterval(interval);
    }, [token]);

    const sortedEmployees = [...employees].sort((a, b) => {
        if (sortBy === "name") return a.name.localeCompare(b.name);
        if (sortBy === "hoursDesc") return b.liveSeconds - a.liveSeconds;
        if (sortBy === "hoursAsc") return a.liveSeconds - b.liveSeconds;
        if (sortBy === "lastPunch") {
            const aTime = a.lastPunch === "—" ? 0 : timeToMinutes(a.lastPunch) || 0;
            const bTime = b.lastPunch === "—" ? 0 : timeToMinutes(b.lastPunch) || 0;
            return bTime - aTime;
        }
        return 0;
    });

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center min-vh-100">
                <div className="spinner-border text-primary" role="status"></div>
            </div>
        );
    }

    return (
        <div className="AttendanceContainer w-100 h-100 px-4 pt-4 pb-5">
            <div className="d-flex justify-content-between align-items-center mb-4">
                <div className="attendance-header">
                    <h3 className="m-0">Real-Time Hours</h3>
                    <p className="text-muted small mb-0">Live working duration tracking with automatic break deduction.</p>
                </div>

                <div className="d-flex gap-2">
                    <div className="btn-group rounded-3 overflow-hidden border">
                        <button className={`btn btn-sm ${viewType === "grid" ? "btn-dark" : "btn-light"}`} onClick={() => setViewType("grid")}>Grid</button>
                        <button className={`btn btn-sm ${viewType === "list" ? "btn-dark" : "btn-light"}`} onClick={() => setViewType("list")}>List</button>
                    </div>
                </div>
            </div>

            <div className="attendance-filters">
                <div className="d-flex align-items-center gap-2">
                    <span className="badge bg-primary bg-opacity-10 text-primary px-3 py-2 rounded-pill">
                        Break: 12:50 PM – 1:30 PM
                    </span>
                    <span className="badge bg-success bg-opacity-10 text-success px-3 py-2 rounded-pill">
                        Live Updating
                    </span>
                </div>

                <div className="d-flex gap-3">
                    <select className="form-select border-0 shadow-sm" value={sortBy} onChange={(e) => setSortBy(e.target.value)} style={{ width: '220px', backgroundColor: 'var(--bg-color)', fontWeight: '600' }}>
                        <option value="name">Sort by Name</option>
                        <option value="hoursDesc">Hours (High → Low)</option>
                        <option value="hoursAsc">Hours (Low → High)</option>
                        <option value="lastPunch">Recent Punch First</option>
                    </select>
                </div>
            </div>

            {viewType === "grid" ? (
                <div className="row g-4">
                    {sortedEmployees.map(emp => (
                        <div key={emp.id} className="col-12 col-md-6 col-xl-4">
                            <div className="attendance-table-wrapper h-100 p-4 transition-all hover-translate-y">
                                <div className="d-flex align-items-center gap-3 mb-4">
                                    <img
                                        src={emp.photo}
                                        alt={emp.name}
                                        className="att-cell-img"
                                        style={{ width: '56px', height: '56px' }}
                                        onError={e => e.target.src = "/assets/user.png"}
                                    />
                                    <div>
                                        <h6 className="mb-0 fw-bold text-main">{emp.name}</h6>
                                        <p className="text-muted small mb-0">{emp.position}</p>
                                    </div>
                                </div>

                                <div className="row g-2 mb-4">
                                    <div className="col-6">
                                        <div className="p-3 rounded-3 bg-light">
                                            <small className="text-muted d-block mb-1">Status</small>
                                            <div className={`status-pill ${emp.isClockedIn ? 'status-active-pill' : 'status-inactive-pill'}`}>
                                                <span className="status-spot"></span>
                                                {emp.isClockedIn ? "IN" : "OUT"}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-6">
                                        <div className="p-3 rounded-3 bg-light">
                                            <small className="text-muted d-block mb-1">Last Punch</small>
                                            <span className="fw-bold text-main">{emp.lastPunch}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="p-3 rounded-4 text-center border" style={{ backgroundColor: 'var(--bg-color)' }}>
                                    <small className="text-muted d-block mb-1">TOTAL WORKED TIME</small>
                                    <div className="fw-bold fs-4" style={{
                                        color: emp.liveSeconds >= 8 * 3600 ? "var(--primary-color)" : emp.isClockedIn ? "#120e4d" : "var(--text-muted)"
                                    }}>
                                        {formatDuration(emp.liveSeconds)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="attendance-table-wrapper">
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Employee</th>
                                    <th>Punch Stat</th>
                                    <th>Last Seen</th>
                                    <th>Total Duration</th>
                                </tr>
                            </thead>
                            <tbody>
                                {sortedEmployees.map(emp => (
                                    <tr key={emp.id}>
                                        <td>
                                            <div className="d-flex align-items-center gap-3">
                                                <img src={emp.photo} alt="" className="att-cell-img" onError={e => e.target.src = "/assets/user.png"} />
                                                <div>
                                                    <div className="att-name-text">{emp.name}</div>
                                                    <small className="text-muted">{emp.position}</small>
                                                </div>
                                            </div>
                                        </td>
                                        <td>
                                            <div className={`status-pill ${emp.isClockedIn ? 'status-active-pill' : 'status-inactive-pill'}`}>
                                                <span className="status-spot"></span>
                                                {emp.isClockedIn ? "Active" : "Inactive"}
                                            </div>
                                        </td>
                                        <td className="fw-bold text-main">{emp.lastPunch}</td>
                                        <td>
                                            <span className="fw-bold fs-6" style={{ color: emp.liveSeconds >= 8 * 3600 ? "var(--primary-color)" : "inherit" }}>
                                                {formatDuration(emp.liveSeconds)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
